/**
 * Care Team PDF — IronLimb Session D.
 *
 * Client-side PDF generation via @react-pdf/renderer. No backend, no PHI
 * leaves the device. The user picks a date range, we pull the relevant
 * residual-limb logs + pain check-ins + activity counts from local sources,
 * compose a printable summary for their PT / prosthetist / clinician.
 *
 * Footer disclaimer explicit: "Generated by Enferno — not a medical record."
 */
import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { padding: 36, fontSize: 10, fontFamily: 'Helvetica', color: '#111' },
  h1: { fontSize: 18, fontWeight: 'bold', marginBottom: 4 },
  h2: { fontSize: 13, fontWeight: 'bold', marginTop: 14, marginBottom: 6, borderBottom: 1, borderColor: '#888', paddingBottom: 2 },
  metaRow: { fontSize: 9, color: '#555', marginBottom: 2 },
  tableHeader: { flexDirection: 'row', borderBottom: 1, borderColor: '#666', paddingBottom: 3, marginTop: 4, marginBottom: 2 },
  tableRow: { flexDirection: 'row', paddingVertical: 2, borderBottom: 0.5, borderColor: '#ddd' },
  cellDate: { width: 60, fontSize: 9 },
  cellSkin: { width: 75, fontSize: 9 },
  cellSocket: { width: 65, fontSize: 9 },
  cellPhantom: { width: 110, fontSize: 9 },
  cellWear: { width: 45, fontSize: 9 },
  cellNote: { flex: 1, fontSize: 9 },
  headerText: { fontSize: 9, fontWeight: 'bold' },
  empty: { fontSize: 10, fontStyle: 'italic', color: '#777', marginTop: 4 },
  footer: { position: 'absolute', bottom: 30, left: 36, right: 36, fontSize: 8, color: '#888', textAlign: 'center', borderTop: 1, borderColor: '#ccc', paddingTop: 6 },
  chart: { height: 80, flexDirection: 'row', alignItems: 'flex-end', marginTop: 6, paddingHorizontal: 8, borderBottom: 1, borderColor: '#888' },
  bar: { backgroundColor: '#7c3aed', marginHorizontal: 1 },
  chartXLabel: { fontSize: 7, color: '#666', marginTop: 2, textAlign: 'center' },
});

// Skin status → label (color removed from PDF since it must read fine in B&W).
const skinLabel = (s) => {
  switch (s) {
    case 'none': return 'All clear';
    case 'minor': return 'Minor irritation';
    case 'concern': return 'Concern';
    case 'serious': return 'Serious';
    default: return '—';
  }
};

const socketLabel = (s) => {
  switch (s) {
    case 'perfect': return 'Perfect';
    case 'loose': return 'Loose';
    case 'tight': return 'Tight';
    case 'pain-point': return 'Pain point';
    default: return '—';
  }
};

const phantomLabel = (p) => {
  if (!p || (p.level === 0 && (!p.sensations || p.sensations.length === 0))) return '—';
  const lv = p.level != null ? `${p.level}/10` : '';
  const sens = (p.sensations || []).join(', ');
  return [lv, sens].filter(Boolean).join(' · ');
};

const CareTeamPDF = ({
  profile,
  variantLabels = [],
  submodes = [],
  startDate,
  endDate,
  residualLogs = [],
  painLogs = [],
  activityCounts = { strength: 0, endurance: 0, combatives: 0 },
  generatedAt,
}) => {
  // Pain chart bars — normalized to chart height 70px
  const maxPain = Math.max(10, ...painLogs.map((p) => p.level || 0));
  const chartBars = painLogs.slice(0, 30); // cap at 30 for visual clarity

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        {/* Header */}
        <Text style={styles.h1}>Enferno — Care Team Summary</Text>
        <Text style={styles.metaRow}>Patient: {profile?.name || '—'}</Text>
        {profile?.dob && <Text style={styles.metaRow}>DOB: {profile.dob}</Text>}
        <Text style={styles.metaRow}>Adaptive submodes: {submodes.length > 0 ? submodes.join(', ') : '—'}</Text>
        <Text style={styles.metaRow}>Prosthesis variants: {variantLabels.length > 0 ? variantLabels.join(', ') : '—'}</Text>
        <Text style={styles.metaRow}>Period: {startDate} → {endDate}</Text>

        {/* Residual-limb log */}
        <Text style={styles.h2}>Residual-limb log ({residualLogs.length} entries)</Text>
        {residualLogs.length === 0 ? (
          <Text style={styles.empty}>No residual-limb log entries in this period.</Text>
        ) : (
          <>
            <View style={styles.tableHeader}>
              <Text style={[styles.cellDate, styles.headerText]}>Date</Text>
              <Text style={[styles.cellSkin, styles.headerText]}>Skin</Text>
              <Text style={[styles.cellSocket, styles.headerText]}>Socket</Text>
              <Text style={[styles.cellPhantom, styles.headerText]}>Phantom</Text>
              <Text style={[styles.cellWear, styles.headerText]}>Wear (h)</Text>
              <Text style={[styles.cellNote, styles.headerText]}>Note</Text>
            </View>
            {residualLogs.map((log) => (
              <View key={log.date} style={styles.tableRow}>
                <Text style={styles.cellDate}>{log.date}</Text>
                <Text style={styles.cellSkin}>{skinLabel(log.skin?.status)}</Text>
                <Text style={styles.cellSocket}>{socketLabel(log.socket?.fit)}</Text>
                <Text style={styles.cellPhantom}>{phantomLabel(log.phantom)}</Text>
                <Text style={styles.cellWear}>{log.wear_time_hours ?? '—'}</Text>
                <Text style={styles.cellNote}>{log.note || (log.skin?.note ? `skin: ${log.skin.note}` : '—')}</Text>
              </View>
            ))}
          </>
        )}

        {/* Pain check-ins */}
        <Text style={styles.h2}>Pain check-ins ({painLogs.length} entries)</Text>
        {painLogs.length === 0 ? (
          <Text style={styles.empty}>No pain check-ins in this period.</Text>
        ) : (
          <>
            <View style={styles.chart}>
              {chartBars.map((p, i) => {
                const heightPct = ((p.level || 0) / maxPain) * 70;
                return (
                  <View
                    key={i}
                    style={[styles.bar, { height: heightPct, width: Math.max(3, 460 / chartBars.length - 2) }]}
                  />
                );
              })}
            </View>
            <Text style={styles.chartXLabel}>
              Earliest ← → Most recent · Y-axis: pain level (0–10) · n={chartBars.length}
            </Text>
            <Text style={[styles.metaRow, { marginTop: 6 }]}>
              Avg: {(painLogs.reduce((s, p) => s + (p.level || 0), 0) / painLogs.length).toFixed(1)}
              {'  ·  '}
              Max: {Math.max(...painLogs.map((p) => p.level || 0))}
              {'  ·  '}
              Days at 7+: {painLogs.filter((p) => (p.level || 0) >= 7).length}
            </Text>
          </>
        )}

        {/* Activity summary */}
        <Text style={styles.h2}>Activity summary</Text>
        <Text style={styles.metaRow}>Strength sessions: {activityCounts.strength}</Text>
        <Text style={styles.metaRow}>Endurance sessions: {activityCounts.endurance}</Text>
        <Text style={styles.metaRow}>Combatives sessions: {activityCounts.combatives}</Text>

        {/* Equipment */}
        <Text style={styles.h2}>Equipment in use</Text>
        <Text style={styles.metaRow}>
          {(profile?.equipment || []).length > 0 ? (profile.equipment || []).join(', ') : 'No equipment selected on profile.'}
        </Text>

        {/* Footer disclaimer */}
        <Text style={styles.footer} fixed>
          Generated by Enferno on {generatedAt} — self-reported data only. Not a medical record. For clinical decisions, defer to qualified providers.
        </Text>
      </Page>
    </Document>
  );
};

export default CareTeamPDF;
